import { useEffect, useState } from 'react';
import { Download, Share, Plus, X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface Props {
  className?: string;
  label?: string;
}

/**
 * Botón "Instalar app" (PWA).
 * - Android/Chrome: usa el evento beforeinstallprompt para instalar con 1 toque.
 * - iOS (Safari): no hay prompt automático → muestra un tutorial para "Agregar a inicio".
 * - Si la app ya está instalada (standalone) o no se puede instalar, no se muestra nada.
 */
export default function InstallAppButton({ className, label = 'Instalar app' }: Props) {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosHelp, setShowIosHelp] = useState(false);

  const isStandalone =
    typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as any).standalone === true);

  const isIos =
    typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Ya instalada → no mostrar
  if (isStandalone) return null;
  // Mostrar solo si hay prompt (Android) o es iOS (tutorial). En desktop sin prompt, no.
  if (!deferred && !isIos) return null;

  const handleClick = async () => {
    if (deferred) {
      await deferred.prompt();
      await deferred.userChoice;
      setDeferred(null);
    } else {
      setShowIosHelp(true);
    }
  };

  const defaultClass =
    'inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-taupe-700 text-cream-50 font-medium hover:bg-taupe-800 transition-colors';

  return (
    <>
      <button type="button" onClick={handleClick} className={className ?? defaultClass}>
        <Download className="h-4 w-4" />
        {label}
      </button>

      {/* Tutorial iOS */}
      {showIosHelp && (
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 p-4"
          onClick={() => setShowIosHelp(false)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-sm p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-taupe-800">Instalar en iPhone / iPad</h3>
              <button onClick={() => setShowIosHelp(false)} className="p-1 text-taupe-400 hover:text-taupe-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-taupe-500 mb-4">
              En iOS se agrega desde <strong>Safari</strong> (no funciona con Chrome). Seguí estos pasos:
            </p>
            <ol className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-taupe-100 text-taupe-700 text-sm font-bold flex items-center justify-center">1</span>
                <span className="text-sm text-taupe-700 flex items-center gap-1.5">
                  Tocá el botón <Share className="inline h-4 w-4 text-blue-600" /> <strong>Compartir</strong> (abajo en Safari).
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-taupe-100 text-taupe-700 text-sm font-bold flex items-center justify-center">2</span>
                <span className="text-sm text-taupe-700 flex items-center gap-1.5">
                  Deslizá y elegí <Plus className="inline h-4 w-4 text-taupe-600" /> <strong>“Agregar a inicio”</strong>.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-taupe-100 text-taupe-700 text-sm font-bold flex items-center justify-center">3</span>
                <span className="text-sm text-taupe-700">
                  Tocá <strong>Agregar</strong> arriba a la derecha. ¡Listo! Queda como app en tu pantalla de inicio.
                </span>
              </li>
            </ol>
            <button
              onClick={() => setShowIosHelp(false)}
              className="mt-5 w-full py-2.5 rounded-xl bg-taupe-700 text-cream-50 font-medium hover:bg-taupe-800"
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </>
  );
}
